import { isIsoDate } from "./dates";
import { DomainError } from "./errors";

// ===== CNAB 240 (Febraban v. 10.7) — cobrança, perfil do Banco do Brasil (T2.10, RF-FIN-04) =====
//
// Remessa: header de arquivo (0), header de lote (1), segmentos P e Q por boleto (3), trailer de lote (5) e trailer de
// arquivo (9). Retorno: os mesmos headers e trailers com os segmentos T e U por ocorrência. Linhas de 240 posições,
// separadas por CRLF. Campos numéricos com zeros à esquerda; alfanuméricos em maiúsculas, sem acento, com brancos à
// direita. O perfil do banco (convênio de 7 dígitos, carteira 17, variação) fica isolado nos campos do convênio:
// outro banco ou o CNAB 400 é outra implementação (TODO).

export const CNAB_LAYOUTS = ["febraban240"] as const;
export type CnabLayout = (typeof CNAB_LAYOUTS)[number];
export const CNAB_LINE = 240;
/** Teto do arquivo de retorno (≈ 4.100 linhas) e de linhas lidas. */
export const CNAB_RETURN_MAX_BYTES = 1_000_000;
export const CNAB_RETURN_MAX_LINES = 5_000;
/** Boletos (registros e pedidos de baixa) por remessa. */
export const CNAB_REMITTANCE_MAX_ITEMS = 500;
/** Valor de um campo de 15 posições com 2 decimais. */
const MAX_FIELD15 = 999_999_999_999_999;

const fail = (code: string, message: string): never => {
  throw new DomainError(code, message);
};

/** Texto do CNAB: sem acento, maiúsculo, só caracteres seguros; cortado/completado com brancos à direita. */
export function cnabText(s: string | null | undefined, width: number): string {
  const clean = (s ?? "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toUpperCase()
    .replace(/[^A-Z0-9 .,/\-&()]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, width);
  return clean.padEnd(width, " ");
}

/** Número com zeros à esquerda; não cabe ou não é inteiro não negativo = erro (nunca corta). */
export function cnabNum(v: number | string, width: number, what = "Campo"): string {
  const s = typeof v === "number" ? (Number.isSafeInteger(v) && v >= 0 ? String(v) : "") : v;
  if (!/^\d+$/.test(s) || s.length > width) fail("validation", `${what} inválido para o CNAB.`);
  return s.padStart(width, "0");
}

const ddmmyyyy = (iso: string) => {
  if (!isIsoDate(iso)) fail("validation", `Data inválida: ${iso}`);
  return `${iso.slice(8, 10)}${iso.slice(5, 7)}${iso.slice(0, 4)}`;
};

/** Agência ou conta com DV opcional: "1234-5" → { number: "1234", digit: "5" }. Só dígitos (DV pode ser X). */
export function splitDigit(s: string, what: string): { number: string; digit: string } {
  const m = /^\s*(\d{1,12})(?:\s*-\s*([\dXx]))?\s*$/.exec(s);
  if (!m) fail("validation", `${what} inválida: use só números, com o dígito depois do hífen (ex.: 1234-5).`);
  return { number: m![1]!, digit: (m![2] ?? " ").toUpperCase() };
}

export type CnabAccount = {
  bankCode: string;
  bankName: string;
  agency: string;
  account: string;
  /** Convênio de cobrança (7 dígitos no BB). */
  agreement: string;
  /** Carteira (2 dígitos) e variação (3 dígitos). */
  wallet: string;
  walletVariation: string;
  companyDocument: string;
  companyName: string;
};

/** Os 20 caracteres do convênio no header (BB: convênio 9, "0014", carteira 2, variação 3, brancos 2). */
function agreementField(a: CnabAccount): string {
  if (!/^\d{7}$/.test(a.agreement)) fail("validation", "Convênio de cobrança precisa de 7 dígitos.");
  if (!/^\d{2}$/.test(a.wallet)) fail("validation", "Carteira precisa de 2 dígitos.");
  if (!/^\d{3}$/.test(a.walletVariation)) fail("validation", "Variação da carteira precisa de 3 dígitos.");
  return `${a.agreement.padStart(9, "0")}0014${a.wallet}${a.walletVariation}  `;
}

const docKind = (doc: string) => (doc.length === 14 ? "2" : doc.length === 11 ? "1" : "0");

function accountFields(a: CnabAccount) {
  const ag = splitDigit(a.agency, "Agência");
  const acc = splitDigit(a.account, "Conta");
  return `${cnabNum(ag.number, 5, "Agência")}${ag.digit}${cnabNum(acc.number, 12, "Conta")}${acc.digit} `;
}

const line = (s: string, what: string) => {
  if (s.length !== CNAB_LINE) fail("validation", `Registro ${what} com ${s.length} posições (esperado 240).`);
  return s;
};

export type RemittanceItem = {
  /** `register` = entrada de título (01); `cancel` = pedido de baixa (02). */
  movement: "register" | "cancel";
  ourNumber: string;
  documentNumber: string;
  issueDate: string;
  dueDate: string;
  amountCents: number;
  payer: {
    document: string;
    name: string;
    address: string;
    district: string;
    zip: string;
    city: string;
    state: string;
  };
};

export type RemittanceResult = { content: string; lines: number; registerCount: number; totalCents: number };

/**
 * Arquivo de remessa CNAB 240 (um lote de cobrança). `generatedAt` em hora de São Paulo (`AAAA-MM-DDTHH:MM:SS`).
 * Total do lote em BigInt; registro fora do formato (campo grande demais, data inválida) recusa o arquivo inteiro.
 */
export function buildRemittance240(
  a: CnabAccount,
  meta: { sequence: number; generatedAt: string },
  items: RemittanceItem[],
): RemittanceResult {
  if (items.length === 0) fail("validation", "Nenhum boleto para a remessa.");
  if (items.length > CNAB_REMITTANCE_MAX_ITEMS)
    fail("validation", `No máximo ${CNAB_REMITTANCE_MAX_ITEMS} boletos por remessa.`);
  const bank = cnabNum(a.bankCode, 3, "Banco");
  const doc = a.companyDocument.replace(/\D/g, "");
  if (doc.length !== 14 && doc.length !== 11) fail("validation", "CPF/CNPJ da empresa inválido para o CNAB.");
  const conv = agreementField(a);
  const accf = accountFields(a);
  const day = meta.generatedAt.slice(0, 10);
  const time = meta.generatedAt.slice(11, 19).replace(/:/g, "");
  if (!/^\d{6}$/.test(time)) fail("validation", "Hora de geração inválida.");
  const nsa = cnabNum(meta.sequence, 6, "Número da remessa");
  const out: string[] = [];
  out.push(
    line(
      `${bank}00000${" ".repeat(9)}${docKind(doc)}${cnabNum(doc, 14, "CNPJ")}${conv}${accf}` +
        `${cnabText(a.companyName, 30)}${cnabText(a.bankName, 30)}${" ".repeat(10)}1${ddmmyyyy(day)}${time}${nsa}` +
        `08300000${" ".repeat(20)}${" ".repeat(20)}${" ".repeat(29)}`,
      "header de arquivo",
    ),
  );
  out.push(
    line(
      `${bank}00011R01  042 ${docKind(doc)}${cnabNum(doc, 15, "CNPJ")}${conv}${accf}${cnabText(a.companyName, 30)}` +
        `${" ".repeat(40)}${" ".repeat(40)}${cnabNum(meta.sequence, 8)}${ddmmyyyy(day)}00000000${" ".repeat(33)}`,
      "header de lote",
    ),
  );
  let seq = 0;
  let total = 0n;
  let registers = 0;
  for (const it of items) {
    if (!/^\d{17}$/.test(it.ourNumber)) fail("validation", "Nosso número inválido para a remessa.");
    if (!Number.isSafeInteger(it.amountCents) || it.amountCents <= 0 || it.amountCents > MAX_FIELD15)
      fail("validation", "Valor do boleto inválido para a remessa.");
    const mov = it.movement === "register" ? "01" : "02";
    if (it.movement === "register") {
      registers++;
      total += BigInt(it.amountCents);
    }
    seq++;
    out.push(
      line(
        `${bank}00013${cnabNum(seq, 5)}P ${mov}${accf}${it.ourNumber.padEnd(20, " ")}71122` +
          `${cnabText(it.documentNumber, 15)}${ddmmyyyy(it.dueDate)}${cnabNum(it.amountCents, 15)}000000` +
          `02N${ddmmyyyy(it.issueDate)}300000000${"0".repeat(15)}000000000${"0".repeat(15)}${"0".repeat(15)}` +
          `${"0".repeat(15)}${cnabText(it.documentNumber, 25)}300000009${"0".repeat(10)} `,
        "segmento P",
      ),
    );
    const pdoc = it.payer.document.replace(/\D/g, "");
    if (pdoc.length !== 11 && pdoc.length !== 14)
      fail("validation", `CPF/CNPJ do pagador inválido no boleto ${it.ourNumber}.`);
    const zip = it.payer.zip.replace(/\D/g, "");
    if (zip.length !== 8) fail("validation", `CEP do pagador inválido no boleto ${it.ourNumber}.`);
    seq++;
    out.push(
      line(
        `${bank}00013${cnabNum(seq, 5)}Q ${mov}${docKind(pdoc)}${cnabNum(pdoc, 15)}${cnabText(it.payer.name, 40)}` +
          `${cnabText(it.payer.address, 40)}${cnabText(it.payer.district, 15)}${zip}${cnabText(it.payer.city, 15)}` +
          `${cnabText(it.payer.state, 2)}0${"0".repeat(15)}${" ".repeat(40)}000${" ".repeat(20)}${" ".repeat(8)}`,
        "segmento Q",
      ),
    );
  }
  if (total > BigInt(MAX_FIELD15)) fail("validation", "Total da remessa acima do limite do arquivo.");
  out.push(
    line(
      `${bank}00015${" ".repeat(9)}${cnabNum(seq + 2, 6)}${cnabNum(registers, 6)}${cnabNum(total.toString(), 17)}` +
        `${"0".repeat(6)}${"0".repeat(17)}${"0".repeat(6)}${"0".repeat(17)}${"0".repeat(6)}${"0".repeat(17)}` +
        `${" ".repeat(8)}${" ".repeat(117)}`,
      "trailer de lote",
    ),
  );
  out.push(
    line(
      `${bank}99999${" ".repeat(9)}000001${cnabNum(seq + 4, 6)}000000${" ".repeat(205)}`,
      "trailer de arquivo",
    ),
  );
  return {
    content: `${out.join("\r\n")}\r\n`,
    lines: out.length,
    registerCount: registers,
    totalCents: Number(total),
  };
}

// ---------- retorno ----------

/** O que cada código de movimento do retorno significa para o ERP. */
export type ReturnMovementKind = "registered" | "rejected" | "paid" | "canceled" | "fee" | "info";
const MOVEMENT_KIND: Readonly<Record<string, ReturnMovementKind>> = {
  "02": "registered",
  "03": "rejected",
  "06": "paid",
  "17": "paid",
  "09": "canceled",
  "28": "fee",
};
export const returnMovementKind = (code: string): ReturnMovementKind =>
  Object.hasOwn(MOVEMENT_KIND, code) ? MOVEMENT_KIND[code]! : "info";

export const RETURN_MOVEMENT_LABEL: Readonly<Record<string, string>> = {
  "02": "Entrada confirmada",
  "03": "Entrada rejeitada",
  "06": "Liquidação",
  "09": "Baixa",
  "17": "Liquidação após baixa",
  "28": "Débito de tarifas",
};

export type ReturnHeader = {
  bankCode: string;
  companyDocument: string;
  agreement: string;
  agency: string;
  account: string;
  sequence: number;
  generatedOn: string;
};

export type ReturnItem = {
  /** Linha do segmento T no arquivo (1 = header). */
  line: number;
  movement: string;
  ourNumber: string;
  documentNumber: string;
  dueDate: string | null;
  nominalCents: number;
  feeCents: number;
  reasons: string;
  interestCents: number;
  discountCents: number;
  rebateCents: number;
  paidCents: number;
  netCents: number;
  occurrenceDate: string | null;
  creditDate: string | null;
};

/** "DDMMAAAA" → AAAA-MM-DD; zeros/brancos = null; data impossível = erro. */
function readDate(s: string, where: string): string | null {
  if (/^[0 ]{8}$/.test(s)) return null;
  if (!/^\d{8}$/.test(s)) fail("invalid_cnab", `Data inválida ${where}.`);
  const iso = `${s.slice(4, 8)}-${s.slice(2, 4)}-${s.slice(0, 2)}`;
  if (!isIsoDate(iso)) fail("invalid_cnab", `Data inválida ${where}.`);
  return iso;
}

function readNum(s: string, where: string): number {
  if (!/^\d+$/.test(s)) fail("invalid_cnab", `Valor não numérico ${where}.`);
  const n = Number(s);
  if (!Number.isSafeInteger(n) || n > MAX_FIELD15) fail("invalid_cnab", `Valor fora do limite ${where}.`);
  return n;
}

const SAFE_LINE = /^[\x20-\x7e\xa0-\xff]{240}$/;

/**
 * Lê o retorno CNAB 240 (texto já decodificado como Latin-1). Confere tamanho e caracteres de cada linha, header de
 * arquivo de retorno (código 2), segmentos T seguidos de U, trailer de arquivo e os campos numéricos usados. Qualquer
 * desvio = `invalid_cnab` com a linha (o arquivo inteiro é recusado; nada é baixado pela metade).
 */
export function parseReturn240(text: string): { header: ReturnHeader; items: ReturnItem[] } {
  if (text.length > CNAB_RETURN_MAX_BYTES) fail("invalid_cnab", "Arquivo de retorno grande demais.");
  const lines = text.split(/\r?\n/);
  while (lines.length && lines.at(-1) === "") lines.pop();
  if (lines.length > CNAB_RETURN_MAX_LINES) fail("invalid_cnab", "Arquivo de retorno com linhas demais.");
  if (lines.length < 4) fail("invalid_cnab", "Arquivo de retorno incompleto.");
  lines.forEach((l, k) => {
    if (!SAFE_LINE.test(l))
      fail(
        "invalid_cnab",
        `Linha ${k + 1} fora do padrão CNAB 240 (240 posições, sem caracteres de controle).`,
      );
  });
  const h = lines[0]!;
  if (h[7] !== "0" || h[142] !== "2")
    fail("invalid_cnab", "O arquivo não é um retorno CNAB 240 (header de arquivo de retorno ausente).");
  if (lines.at(-1)![7] !== "9") fail("invalid_cnab", "Trailer de arquivo ausente.");
  const bankCode = h.slice(0, 3);
  if (!/^\d{3}$/.test(bankCode)) fail("invalid_cnab", "Banco inválido no header.");
  const header: ReturnHeader = {
    bankCode,
    companyDocument: h.slice(18, 32),
    agreement: h.slice(32, 41).replace(/^0+/, ""),
    agency: h.slice(52, 57).replace(/^0+/, ""),
    account: h.slice(58, 70).replace(/^0+/, ""),
    sequence: readNum(h.slice(157, 163), "no NSA do header"),
    generatedOn: readDate(h.slice(143, 151), "no header") ?? fail("invalid_cnab", "Data de geração ausente."),
  };
  const items: ReturnItem[] = [];
  for (let k = 1; k < lines.length - 1; k++) {
    const l = lines[k]!;
    if (l.slice(0, 3) !== bankCode) fail("invalid_cnab", `Linha ${k + 1} de outro banco.`);
    if (l[7] !== "3") continue;
    const seg = l[13];
    if (seg === "U") fail("invalid_cnab", `Segmento U sem o T antes (linha ${k + 1}).`);
    if (seg !== "T") continue;
    const u = lines[k + 1];
    if (!u || u[7] !== "3" || u[13] !== "U")
      fail("invalid_cnab", `Segmento T sem o U em seguida (linha ${k + 1}).`);
    const at = (where: string) => `na linha ${k + 1}${where}`;
    const movement = l.slice(15, 17);
    if (!/^\d{2}$/.test(movement) || u!.slice(15, 17) !== movement)
      fail("invalid_cnab", `Código de movimento inválido ${at("")}.`);
    const ourNumber = l.slice(37, 57).trim();
    if (!/^\d{1,20}$/.test(ourNumber)) fail("invalid_cnab", `Nosso número inválido ${at("")}.`);
    items.push({
      line: k + 1,
      movement,
      ourNumber,
      documentNumber: l.slice(58, 73).trim(),
      dueDate: readDate(l.slice(73, 81), at(" (vencimento)")),
      nominalCents: readNum(l.slice(81, 96), at(" (valor do título)")),
      feeCents: readNum(l.slice(198, 213), at(" (tarifa)")),
      reasons: l.slice(213, 223).trim(),
      interestCents: readNum(u!.slice(17, 32), at(" (juros/multa)")),
      discountCents: readNum(u!.slice(32, 47), at(" (desconto)")),
      rebateCents: readNum(u!.slice(47, 62), at(" (abatimento)")),
      paidCents: readNum(u!.slice(77, 92), at(" (valor pago)")),
      netCents: readNum(u!.slice(92, 107), at(" (valor líquido)")),
      occurrenceDate: readDate(u!.slice(137, 145), at(" (data da ocorrência)")),
      creditDate: readDate(u!.slice(145, 153), at(" (data do crédito)")),
    });
    k++;
  }
  return { header, items };
}

/**
 * Baixa de uma liquidação do retorno: principal = pago − juros/multa + desconto + abatimento. Principal maior que o
 * saldo, título fechado ou valor zerado = revisão (nada é baixado). Menor que o saldo = baixa parcial.
 */
export function returnSettlement(i: {
  paidCents: number;
  interestCents: number;
  discountCents: number;
  rebateCents: number;
  openCents: number;
  titleOpen: boolean;
}):
  | { review: null; amountCents: number; interestCents: number; discountCents: number; partial: boolean }
  | { review: "title_closed" | "overpaid" | "invalid_amount" } {
  if (!i.titleOpen || i.openCents <= 0) return { review: "title_closed" };
  const principal =
    BigInt(i.paidCents) - BigInt(i.interestCents) + BigInt(i.discountCents) + BigInt(i.rebateCents);
  if (i.paidCents <= 0 || principal <= 0n) return { review: "invalid_amount" };
  if (principal > BigInt(i.openCents)) return { review: "overpaid" };
  return {
    review: null,
    amountCents: Number(principal),
    interestCents: i.interestCents,
    discountCents: i.discountCents + i.rebateCents,
    partial: principal < BigInt(i.openCents),
  };
}

/** Chave de idempotência de uma ocorrência do retorno (por conta): nosso número + movimento + data + valor pago. */
export const returnDedupeKey = (it: ReturnItem): string =>
  `${it.ourNumber}|${it.movement}|${it.occurrenceDate ?? ""}|${it.paidCents}`;

export type SimulatedReturnItem = {
  movement: string;
  ourNumber: string;
  documentNumber: string;
  dueDate: string;
  nominalCents: number;
  feeCents: number;
  interestCents: number;
  discountCents: number;
  paidCents: number;
  occurrenceDate: string;
  creditDate: string | null;
};

/**
 * Retorno CNAB 240 no formato que o banco devolve (segmentos T/U). Serve aos testes e à simulação do ambiente de
 * testes ("Simular retorno"): nunca é lido como verdade sem passar pelo `parseReturn240`.
 */
export function buildReturn240(
  a: CnabAccount,
  meta: { sequence: number; generatedOn: string },
  items: SimulatedReturnItem[],
): string {
  const bank = cnabNum(a.bankCode, 3, "Banco");
  const doc = a.companyDocument.replace(/\D/g, "");
  const conv = agreementField(a);
  const accf = accountFields(a);
  const out: string[] = [];
  out.push(
    line(
      `${bank}00000${" ".repeat(9)}${docKind(doc)}${cnabNum(doc, 14, "CNPJ")}${conv}${accf}` +
        `${cnabText(a.companyName, 30)}${cnabText(a.bankName, 30)}${" ".repeat(10)}2${ddmmyyyy(meta.generatedOn)}000000` +
        `${cnabNum(meta.sequence, 6)}08300000${" ".repeat(69)}`,
      "header de arquivo",
    ),
  );
  out.push(
    line(
      `${bank}00011T01  042 ${docKind(doc)}${cnabNum(doc, 15, "CNPJ")}${conv}${accf}${cnabText(a.companyName, 30)}` +
        `${" ".repeat(80)}${cnabNum(meta.sequence, 8)}${ddmmyyyy(meta.generatedOn)}00000000${" ".repeat(33)}`,
      "header de lote",
    ),
  );
  let seq = 0;
  for (const it of items) {
    seq++;
    out.push(
      line(
        `${bank}00013${cnabNum(seq, 5)}T ${it.movement}${accf}${it.ourNumber.padEnd(20, " ")}7` +
          `${cnabText(it.documentNumber, 15)}${ddmmyyyy(it.dueDate)}${cnabNum(it.nominalCents, 15)}` +
          `001000000${" ".repeat(25)}09${"0".repeat(16)}${" ".repeat(40)}${"0".repeat(10)}` +
          `${cnabNum(it.feeCents, 15)}${" ".repeat(10)}${" ".repeat(17)}`,
        "segmento T",
      ),
    );
    seq++;
    const net = it.paidCents - it.feeCents;
    out.push(
      line(
        `${bank}00013${cnabNum(seq, 5)}U ${it.movement}${cnabNum(it.interestCents, 15)}${cnabNum(it.discountCents, 15)}` +
          `${"0".repeat(15)}${"0".repeat(15)}${cnabNum(it.paidCents, 15)}${cnabNum(Math.max(0, net), 15)}` +
          `${"0".repeat(15)}${"0".repeat(15)}${ddmmyyyy(it.occurrenceDate)}` +
          `${it.creditDate ? ddmmyyyy(it.creditDate) : "00000000"}${" ".repeat(4)}${"0".repeat(8)}${"0".repeat(15)}` +
          `${" ".repeat(30)}000${" ".repeat(20)}${" ".repeat(7)}`,
        "segmento U",
      ),
    );
  }
  out.push(line(`${bank}00015${" ".repeat(9)}${cnabNum(seq + 2, 6)}${" ".repeat(217)}`, "trailer de lote"));
  out.push(
    line(
      `${bank}99999${" ".repeat(9)}000001${cnabNum(seq + 4, 6)}000000${" ".repeat(205)}`,
      "trailer de arquivo",
    ),
  );
  return `${out.join("\r\n")}\r\n`;
}
